import React, { useEffect } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
// scss
import './scss/deleted-resource.style.scss';
import { DeletedResourceState, SelectType } from './deleted-resource-state';
import CardView from '../../component/card/card-view';
import i18n from '../../i18n';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import { Box, Grid, IconButton, Tooltip } from '@mui/material';
import CommonInput from '../../component/form/common-input.component';
import TableComponent, { HeadCell, TableRowFeatures } from '../../component/table/table.component';
import { Group } from '../../models/group.entity';
import { Restaurant } from '../../models/restaurant.entity';
import _ from 'lodash';
import DeletedDetailDialog from './deleted-detail-dialog';
import DeleteDialogComponent from '../../component/dialog/delete-dialog.component';
import { EditAccount, editAccountMobx } from '../group/account-manage/edit-account';
import { groupUnitData } from '../group/group-unit';
import { useTranslation } from 'react-i18next';
import { Reseller, Staff } from '../../models';
import ReplayIcon from '@mui/icons-material/Replay';
import ButtonComponent from '../../component/button/button.component';
import { addResellerFilter } from '../../constants/tool.reseller';
import { formatEUR } from '../../constants/tool.fn';
import { dateFormat } from '../../constants/utils';


/**
 * 资源回收站页面
 */
const DeletedResource = observer(() => {

    // 使用翻译
    useTranslation();

    // 使用数据管理
    const deletedResourceData = useLocalObservable(() => new DeletedResourceState());



    // 账号详情骨架屏控制
    // const [skeleton, setSkeleton] = useState<boolean>(false);

    useEffect(() => {
        deletedResourceData.init();
        const topbarState = MobxObjectsManager.get(TopBarState);
        topbarState.updateDeletedResourceTitle();
        return () => {
            MobxObjectsManager.remove(DeletedResourceState);
        }
    }, []);

    // 解构数据
    const {
        selectValue,
        isTableLoading,
        tableLists,
        dialogConfig,
        confirmDialogConfig,
        accountDialogConfig,
        accountViewData,
        skeleton,
        buttonsMap
    } = deletedResourceData;

    // 下拉框 集团 | 餐厅 | 账号  静态数据
    let filterList: Array<{
        value: string;
        label: string;
    }> = [{
        value: 'group',
        label: i18n.t('deleted_resource_page_text_group')
    },
    {
        value: 'restaurant',
        label: i18n.t('deleted_resource_page_text_restaurant')
    }, {
        value: 'account',
        label: i18n.t('deleted_resource_page_text_account')
    }];

    filterList = addResellerFilter(filterList)

    // 页面表头数据格式
    const tableHead: {
        group: HeadCell[],
        restaurant: HeadCell[],
        account: HeadCell[],
        reseller: HeadCell[]
    } = {
        // 集团表头集合
        group: [
            {
                id: 'belonged_reseller', label: i18n.t('reseller_page_text_reseller'), renderFunction: (row: Group) => {
                    const reseller = _.get(row, 'belonged_reseller.[0]') as Reseller | undefined;
                    return reseller ? `${reseller.first_name} ${reseller.last_name}` : 'Admin';
                }
            },
            { id: 'nickname', label: i18n.t('group_management_page_text_group_name'), sortStatus: deletedResourceData.getSortStatus('nickname') },
            { id: 'string_id', label: i18n.t('group_management_page_text_group_identification_code') },
            {
                id: 'supervisor', label: i18n.t('group_management_page_text_company_representative'),
                renderFunction: (row: Group) => {
                    // 集团负责人
                    let supervisor = ''
                    row?.supervisor?.forEach((item: Staff) => {
                        supervisor = (supervisor && supervisor + ',') + (item?.first_name || '') + (item?.last_name || '');
                    });
                    return <>{supervisor}</>
                }
            },
            { id: 'reg_city', label: i18n.t('group_management_page_text_city') },
            { id: 'reg_vat_number', label: i18n.t('restaurant_info_page_text_vat_number') },
            {
                id: 'tel', label: i18n.t('general_text_phone'),
                renderFunction: (row: Group) => {
                    return <>{row?.phone_prefix} {row?.tel}</>
                },
            },
            {
                id: 'deleted_date', 
                label: i18n.t('deleted_resource_page_text_deleted_time'),
                 sortStatus: deletedResourceData.getSortStatus('deleted_date'),
                 renderFunction: (row) => dateFormat(row.deleted_date, 'HH:mm:ss DD/MM/YYYY')
            },
            {
                id: '8', label: '',
                renderFunction: (row) => {
                    return renderFeaturesButtons(row);
                }
            }
        ],
        // 餐厅表头集合
        restaurant: [
            {
                id: 'belonged_reseller', label: i18n.t('reseller_page_text_reseller'), renderFunction: (row: Group) => {
                    const reseller = _.get(row, 'belonged_reseller.[0]') as Reseller | undefined;
                    return reseller ? `${reseller.first_name} ${reseller.last_name}` : 'Admin';
                }
            },
            { id: 'nickname', label: i18n.t('restaurant_management_page_table_text_restaurant_name'), sortStatus: deletedResourceData.getSortStatus('nickname') },
            { id: 'string_id', label: i18n.t('restaurant_page_input_text_restaurant_identification_code') },
            { id: 'city', label: i18n.t('restaurant_management_page_table_text_city'), sortStatus: deletedResourceData.getSortStatus('city') },
            {
                id: 'tel', label: i18n.t('restaurant_management_page_table_text_phone'),
                renderFunction: (row: Restaurant) => {
                    return <>{row?.tel_prefix} {row?.tel}</>
                },
            },
            { id: 'reg_vat_number', label: i18n.t('restaurant_info_page_text_vat_number') },
            {
                id: 'deleted_date', 
                label: i18n.t('deleted_resource_page_text_deleted_time'), 
                sortStatus: deletedResourceData.getSortStatus('deleted_date'),
                renderFunction: (row) => dateFormat(row.deleted_date, 'HH:mm:ss DD/MM/YYYY')
            },
            {
                id: '6', label: '',
                renderFunction: (row) => {
                    return renderFeaturesButtons(row);
                }
            }
        ],
        // 账号表头集合
        account: [
            { id: 'nickname', label: i18n.t('general_table_text_staff_name') },
            { id: 'email', label: i18n.t('general_text_email') },
            {
                id: 'phone', label: i18n.t('general_text_phone'),
                renderFunction: (row: Staff) => {
                    return <>{row?.phone_prefix} {row?.phone}</>
                },
            },
            {
                id: '4', label: i18n.t('restaurant_info_page_table_text_associated_restaurant'),
                renderFunction: (row) => {
                    // 员工绑定的餐厅和集团
                    const responsibilities: string[] = []
                    if (row.belonged_restaurant) {
                        row.belonged_restaurant.forEach((item: { nickname: string }) => {
                            responsibilities.push(item.nickname)
                        })
                    }
                    return <>{_.join(responsibilities, ',')}</>
                }
            },
            {
                id: 'deleted_date', 
                label: i18n.t('deleted_resource_page_text_deleted_time'), 
                sortStatus: deletedResourceData.getSortStatus('deleted_date'),
                renderFunction: (row) => dateFormat(row.deleted_date, 'HH:mm:ss DD/MM/YYYY')
            },
            {
                id: '6', label: '', sortStatus: -1,
                renderFunction: (row) => {
                    return renderFeaturesButtons(row);
                }
            }
        ],
        // 经销商表头集合
        reseller: [
            { id: 'nickname', label: i18n.t('general_text_name') },
            { id: 'phone_prefix_phone', label: i18n.t('create_group_component_message_invalid_phone'), renderFunction: (row: Reseller) => `${row?.phone_prefix} ${(row?.phone || '')}` },
            { id: 'city', label: i18n.t('group_management_page_text_city') },
            { id: 'reg_vat_number', label: i18n.t('restaurant_info_page_text_vat_number') },
            { id: 'balance', label: i18n.t('reseller_page_reseller_balance'), renderFunction: (row: Reseller) => formatEUR(Number(row?.balance ?? 0)) },
            {
                id: 'group_detail', label: `${i18n.t('group_management_page_text_group')} / ${i18n.t('group_management_page_text_restaurant')}`,
                renderFunction: (row: Reseller) => {
                    return `${(row?.responsible_group || []).length} / ${(row?.responsible_restaurant || []).length}`

                }
            },
            {
                id: 'reseller_discount', label: i18n.t('restaurant_info_page_text_discount_information'),
                renderFunction: (row: Reseller) => {
                    return <>
                        {`${row?.reseller_discount ?? 100} %`}
                    </>
                }
            },
            {
                id: 'deleted_date',
                label: i18n.t('deleted_resource_page_text_deleted_time'),
                sortStatus: deletedResourceData.getSortStatus('deleted_date'),
                renderFunction: (row) => dateFormat(row.deleted_date, 'HH:mm:ss DD/MM/YYYY')
            },
            {
                id: '6', label: '', sortStatus: -1,
                renderFunction: (row) => {
                    return renderFeaturesButtons(row);
                }
            }
        ]

    }

    // *********************
    // Extra Function
    // *********************

    /**
     * 渲染按钮组
     * @param row @typedef Group 集团类型 | @typedef Restaurant 餐厅类型 | @typedef Staff 账号类型
     * @param btnType 按钮组类型
     * @returns
     */
    const renderFeaturesButtons = (row: any) => {
        const buttons = buttonsMap[selectValue](row);
        return (
            <TableRowFeatures
                buttons={buttons}
            />
        )
    };


    // 渲染页面内容
    const renderContent = () => {
        // 二次确认弹窗选项
        const dialogOptions = ['group', 'reseller'].includes(deletedResourceData.selectValue) && confirmDialogConfig.type === 'RESTORE' ? {
            confirmText: i18n.t('general_text_yes'),
            otherActionBtn: (
                <ButtonComponent
                    size='medium'
                    color='primary'
                    variant='contained'
                    extraButtonStyle={'button-group'}
                    async={true}
                    handleClick={async () => {
                        await confirmDialogConfig?.handleClick?.(false)
                    }}
                >{i18n.t('general_text_no')}</ButtonComponent>
            )
        } : {}

        return (
            <Box className={'contain'}>
                <Grid container>
                    {/* 下拉框 */}
                    <Grid item xs={12}>
                        <Box className='table-top-tool'>
                            <CommonInput
                                inputType={'SELECT'}
                                select={true}
                                items={filterList ?? []}
                                className={'select-input'}
                                size={'small'}
                                value={selectValue}
                                onChange={(e) => {
                                    const selectVal = e.target.value;
                                    deletedResourceData.setSelectValue(selectVal as SelectType);
                                }}
                            />
                        </Box>
                    </Grid>
                    {/* 表格列表 */}
                    <Grid item xs={12}>
                        {/* 资源表格 */}
                        <TableComponent
                            {...{
                                showChecked: 1,
                                list: tableLists[selectValue] ?? [],
                                // 开启表格自定义页脚
                                isUseCustomPagination: true,
                                headCellList: tableHead[selectValue],
                                isTableLoading,
                                isPagination: true,
                                handleSort: (headCell) => {
                                    deletedResourceData._handleSort(headCell);
                                },
                                handleDeleteAll: async (selected) => {
                                    await deletedResourceData._handleDelete(selected, true);
                                },
                                allCount: tableLists[selectValue]?.length ?? 0,
                                extraClass: 'extraTable',
                                renderToolBarRight: (selected) => (
                                    <Tooltip title={i18n.t('recycle_bin_page_text_restore_btn')}>
                                        <IconButton onClick={() => deletedResourceData.handleRestoreDialog(selected)}>
                                            <ReplayIcon style={{ color: '#fff' }} />
                                        </IconButton>
                                    </Tooltip>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
                {/* 表格详情弹窗 集团 | 餐厅 */}
                <DeletedDetailDialog
                    // 数据使用
                    useDeleteData={deletedResourceData}
                    // 弹窗配置
                    dialogConfigData={{
                        // 控制打开
                        openDialog: dialogConfig.visible ?? false,
                        // 弹窗内容类型
                        dialogType: selectValue,
                        // 控制关闭
                        onCloseDialog: () => dialogConfig?.onClose?.(),
                        // 选择的id
                        selectId: dialogConfig?.selectId || '',
                        // 所属集团
                        belonged_group: dialogConfig?.belonged_group || ''
                    }}
                />
                {/* 账户详情弹窗 */}
                <EditAccount
                    open={accountDialogConfig.visible || false}
                    onClose={() => {
                        // 更新弹窗
                        deletedResourceData.upDateAccountDialog({ visible: false });
                        // 关闭禁用
                        editAccountMobx.updateDisable(false);
                        // 清除选中的数据
                        groupUnitData.clearGroupData();
                    }}
                    skeleton={skeleton}
                    editing={accountViewData || {}}
                />


                {/* 删除弹窗 是否删除所选择项目 */}
                <DeleteDialogComponent
                    {...{
                        title: i18n.t('general_text_tips'),
                        desc: confirmDialogConfig.desc,
                        open: confirmDialogConfig.visible ?? false,
                        onCancel: () => {
                            deletedResourceData.updateConfirmDialogConfig({
                                visible: false,
                            });
                        },
                        // 确认删除
                        onConfirm: async () => {
                            await confirmDialogConfig?.handleClick?.(true);
                        }
                    }}
                    {...dialogOptions}
                />
            </Box>
        )
    }

    // *********************
    // Render View
    // *********************

    return (
        <CardView
            {...{
                className: 'deleted-resource-page',
                contentRender: renderContent()
            }}
        />
    )

});

export default DeletedResource;